// web/src/components/ModuleMapModal/_model.tsx —— 内部：Feature 视图模型与常量 + 最近访问读写 + 可见性判定
// 拆自 web/src/components/ModuleMapModal.tsx 的同名分区（内容原样搬运）；由 web/src/components/ModuleMapModal.tsx 以 export * 聚合，导出面对调用方不变。

import { useEffect, useMemo, useRef, useState, type ComponentRef, type ReactNode } from 'react';
import {
  IconApps,
  IconCloud,
  IconCustomerService,
  IconDesktop,
  IconFile,
  IconHistory,
  IconHome,
  IconSearch,
  IconSettings,
  IconThunderbolt,
} from '@arco-design/web-react/icon';
import { NAV_ANCHORS, featureByPathname, groupedNav, type Feature, type NavAnchor } from '../../registry/features';
import { canSeeEntry, canSeePermission } from '../../registry/access';
import {
  buildSearchIndex,
  commonEntries,
  searchFeatureIndex,
  suggestSimilar,
  type EntryKind,
  type SearchEntry,
  type SearchHit,
} from '../../registry/featureSearch';
import PillTag, { type PillColor } from '../PillTag';
export interface FeaturePoint {
  /** Feature id（页内 Tab 的「最近访问」键要与检索索引同形：`<featureId>#<tabKey>`） */
  id: string;
  /** 条目在检索索引里的 key（`feature:<id>` / `anchor:<route>`）：点功能点也进「最近访问」，与点 Tab 同口径 */
  entryKey: string;
  name: string;
  path: string;
  tabs: { key: string; title: string }[];
}

export interface FeatureModule {
  key: string;
  title: string;
  icon: ReactNode;
  features: FeaturePoint[];
}

export interface MapSection {
  id: string;
  title: string;
  icon: ReactNode;
  modules: FeatureModule[];
}

/** 分区图标（仅图标，配色交给 `.bcc-help-section--<id>` 的 CSS，跟随主题 token） */
export const SECTION_ICONS: Record<string, ReactNode> = {
  workbench: <IconCustomerService />,
  business: <IconApps />,
  platform: <IconCloud />,
  system: <IconSettings />,
};

/** 结果行类型标识：图标给「它是什么」，胶囊只在需要区分时给文字（页内 Tab / 面板 / 全局动作） */
export const ENTRY_KIND: Record<EntryKind, { icon: ReactNode; label?: string; color: PillColor }> = {
  anchor: { icon: <IconHome />, color: 'arcoblue' },
  page: { icon: <IconFile />, color: 'arcoblue' },
  tab: { icon: <IconApps />, label: '页内 Tab', color: 'arcoblue' },
  panel: { icon: <IconDesktop />, label: '面板', color: 'gray' },
  action: { icon: <IconThunderbolt />, label: '全局动作', color: 'gold' },
};

/** 单个功能默认展开的页内 Tab 数（超出收成「+N」；知识库这类 15 个 Tab 的域不再把卡片撑到两屏高） */
export const TAB_PREVIEW = 4;
/** 强匹配结果的展示上限，超出提示继续输入缩小范围 */
export const STRONG_CAP = 10;

/** 最近访问（本地记忆：重复找同一个功能不必再搜一次） */
export const RECENT_STORAGE = 'bcc.map.recent';
export const RECENT_MAX = 8;

export function loadRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_STORAGE) || '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

/** 可见性判据统一在 `registry/access.ts`（侧栏 / 功能地图 / 路由守卫同源，勿在此另写一份） */
export function canSee(f: Feature, role: string, perms: Set<string>): boolean {
  return canSeePermission(f.permission, role, perms);
}
export function canSeeAnchor(a: NavAnchor, role: string, perms: Set<string>, agentBound: boolean): boolean {
  return canSeeEntry(a, role, perms, { agentBound });
}

